import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { userSelectors } from 'reducers';
import { subscriptionStatus, RoleNames, conventionStatus, docTypes } from 'utils/constants';
import { useGetDocURLById, useSmallScreen } from 'utils/hooks';
import { texts } from 'utils/languages/fr';
import {
    useGetAggregatedDistibutorDataQuery,
    useGetDistributorSubscribersDataQuery,
    useGetDocumentsQuery,
    useGetInvestmentFundsQuery,
} from 'api/main';

import { customTotalParser } from 'containers/dashboard/admin-distributors/AdminDistributorDashboard';
import { DashboardSection, InfoCard, DashboardTemplate } from '../dashboard/common-components';
import CommercialisedProductCard from './CommercialisedProductCard';
import { CustomCarousel, Loader } from 'components';
import { Box, Chip, Grid, Typography } from '@mui/material';
import OngoingSubscriptionsTable from './OngoingSubscriptionsTable';

const gridItemProps = {
    xs: 12,
    lg: 8,
    tablet: 4,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
};

const DistributorHomepage = () => {
    const history = useHistory();
    const isTablet = useSmallScreen('tablet');

    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);

    const { data: documents = [], isFetching: docLoader } = useGetDocumentsQuery({
        type: docTypes.UPCOMING_PRODUCTS,
    });
    const { src: previewSrc, loading: previewLoading } = useGetDocURLById(documents?.[0]?.id);

    const isAdmin = useMemo(
        () =>
            currentSubscription?.role.name === RoleNames.ADMIN_CIF ||
            currentSubscription?.role.name === RoleNames.PSI,
        [currentSubscription]
    );

    const subscriptionFinished = useMemo(
        () => currentSubscription?.subscriptionStatus === subscriptionStatus.FINISHED,
        [currentSubscription]
    );

    const canPreviewDistributorClientSpace = useMemo(
        () => currentSubscription?.distributorCompany?.documentsValidated,
        [currentSubscription]
    );

    const fullySignedConventions = useMemo(
        () =>
            currentSubscription?.distributorCompany?.conventions?.filter(
                c => c.status === conventionStatus.FULLY_SIGNED
            ),
        [currentSubscription]
    );

    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery({
        visible: 1,
    });
    const { data: ongoingSubscriptionData = [], isFetching: ongoingDataLoading } =
        useGetDistributorSubscribersDataQuery({
            subscriptionStatus: subscriptionStatus.IN_PROGRESS,
        });

    const { data: finishedSubscriptionData = [], isFetching: finishedDataLoading } =
        useGetDistributorSubscribersDataQuery({
            subscriptionStatus: subscriptionStatus.FINISHED,
        });

    const { data: distributorData = {}, isLoading: dataLoader } =
        useGetAggregatedDistibutorDataQuery(
            {},
            {
                skip:
                    currentSubscription.indirectUntransferred ||
                    (!subscriptionFinished && !canPreviewDistributorClientSpace),
            }
        );
    const fundsWithFullConventions = useMemo(
        () =>
            fundList.filter(
                f =>
                    f.enabled &&
                    f.visible &&
                    fullySignedConventions.find(c => c.investmentFund.id === f.id)
            ),
        [fullySignedConventions, fundList]
    );
    const fundsWithPartialOrNoConventions = useMemo(
        () =>
            fundList.filter(
                f =>
                    f.enabled &&
                    f.visible &&
                    !fullySignedConventions.find(c => c.investmentFund.id === f.id)
            ),
        [fullySignedConventions, fundList]
    );

    const nrSubscriptionsPendingCifValidation = useMemo(
        () => ongoingSubscriptionData?.nr_subscriptions_pending_cif_validation,
        [ongoingSubscriptionData]
    );
    const totalOngoingSubscriptions = useMemo(
        () => customTotalParser(ongoingSubscriptionData, 'all', 'total_subscriptions'),

        [ongoingSubscriptionData]
    );
    const totalFinishedSubscriptions = useMemo(
        () => customTotalParser(finishedSubscriptionData, 'all', 'total_subscriptions'),
        [finishedSubscriptionData]
    );

    const handleInfoCardFilter = useCallback(
        path => () => {
            history.push(path);
        },
        [history]
    );

    return (
        <DashboardTemplate showContactDialog>
            <DashboardSection title={isAdmin ? texts.CONSULTANT_ACTIVITY : texts.YOUR_ACTIVITY}>
                <Loader
                    loading={
                        dataLoader ||
                        ongoingDataLoading ||
                        finishedDataLoading ||
                        docLoader ||
                        !currentSubscription.distributorCompany
                    }
                    marginTop={10}
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexDirection={isTablet ? 'column' : 'row'}
                        gap={2}
                    >
                        <InfoCard
                            disableGrid
                            boxProps={{ flex: '1 1', ...(isTablet && { width: '100%' }) }}
                            onClick={handleInfoCardFilter('/subscriptions')}
                            number={totalOngoingSubscriptions}
                            label={texts.ONGOING_SUBSCRIPTIONS}
                        />
                        {isAdmin &&
                            currentSubscription?.distributorCompany?.validateSubscriptions && (
                                <InfoCard
                                    disableGrid
                                    boxProps={{ flex: '1 1', ...(isTablet && { width: '100%' }) }}
                                    number={nrSubscriptionsPendingCifValidation}
                                    onClick={handleInfoCardFilter(
                                        '/subscriptions?pendingCIFValidation=1'
                                    )}
                                    label={texts.SUBSCRIPTIONS_TO_VALIDATE}
                                />
                            )}
                        <InfoCard
                            disableGrid
                            boxProps={{ flex: '1 1', ...(isTablet && { width: '100%' }) }}
                            onClick={handleInfoCardFilter('/investors')}
                            number={totalFinishedSubscriptions}
                            label={texts.FINISHED_SUBSCRIPTIONS}
                        />
                    </Box>
                </Loader>
            </DashboardSection>
            <DashboardSection title={texts.COMMERCIALISATION}>
                <Loader loading={fundsLoader}>
                    <Grid
                        container
                        alignItems="flex-end"
                        justifyContent="space-around"
                        flexWrap={isTablet ? 'wrap' : 'nowrap'}
                        gap={2}
                    >
                        <Grid item {...gridItemProps}>
                            <Typography
                                fontSize={18}
                                variant="body1"
                                gutterBottom
                                align="center"
                                flex="0 1"
                            >
                                {texts.COMMERCIALISED_PRODUCTS}
                            </Typography>
                            {fundsWithFullConventions.length ? (
                                <CustomCarousel>
                                    {fundsWithFullConventions.map(f => (
                                        <CommercialisedProductCard
                                            key={f.id}
                                            fund={f}
                                            status="owned"
                                        />
                                    ))}
                                </CustomCarousel>
                            ) : (
                                <Typography
                                    variant="body1"
                                    align="center"
                                    sx={{
                                        height: '320px',
                                        pt: '45%',
                                        px: '10px',
                                        backgroundColor: 'common.white',
                                    }}
                                >
                                    {texts.NO_CONVENTIONS}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item {...gridItemProps}>
                            <Typography
                                fontSize={18}
                                variant="body1"
                                gutterBottom
                                align="center"
                                flex="0 1"
                            >
                                {texts.OTHER_AVAILABLE_PRODUCTS}
                            </Typography>
                            {fundsWithPartialOrNoConventions.length ? (
                                <CustomCarousel>
                                    {fundsWithPartialOrNoConventions.map(f => (
                                        <CommercialisedProductCard
                                            key={f.id}
                                            fund={f}
                                            status="available"
                                            company={currentSubscription?.distributorCompany}
                                        />
                                    ))}
                                </CustomCarousel>
                            ) : (
                                <Typography
                                    variant="body1"
                                    align="center"
                                    sx={{
                                        height: '320px',
                                        pt: '45%',
                                        px: '10px',
                                        backgroundColor: 'common.white',
                                    }}
                                >
                                    {texts.NO_AVAILABLE_PRODUCTS}
                                </Typography>
                            )}
                        </Grid>
                        {!!documents?.[0]?.id && (
                            <Grid item {...gridItemProps}>
                                <Loader loading={previewLoading} marginTop={0} size={20}>
                                    <Box
                                        flex="1 1 320px"
                                        display="flex"
                                        alignItems="center"
                                        padding={2}
                                        backgroundColor="common.white"
                                    >
                                        <Box
                                            src={previewSrc}
                                            width="100%"
                                            maxHeight="100%"
                                            display="flex"
                                            component="img"
                                        />
                                    </Box>
                                </Loader>
                            </Grid>
                        )}
                    </Grid>
                </Loader>
            </DashboardSection>
            <DashboardSection title={texts.ONGOING_SUBSCRIPTIONS.toUpperCase()}>
                <Typography
                    variant="body1"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 18,
                        mb: 2,
                        gap: 1,
                    }}
                >
                    <Chip
                        color="primary"
                        component="span"
                        sx={{ color: 'common.white' }}
                        label={distributorData?.indirect_subscriptions_pending_transfer || 0}
                    />{' '}
                    {texts.SUBSCRIPTIONS_TO_CONTINUE}
                </Typography>

                <OngoingSubscriptionsTable type="untransferred" />

                <Typography
                    variant="body1"
                    sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, fontSize: 18 }}
                >
                    <Chip
                        label={
                            distributorData?.indirect_subscriptions_transferred_pending_activation ||
                            0
                        }
                        component="span"
                        sx={{ color: 'common.white' }}
                        color="primary"
                    />{' '}
                    {texts.CLIENT_SUBSCRIPTIONS_TO_FINALIZE}
                </Typography>
                <OngoingSubscriptionsTable type="transferred" />
            </DashboardSection>
        </DashboardTemplate>
    );
};

export default DistributorHomepage;
